import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryAPI } from '../services/api';
import { useMessagingSocket } from '../hooks/useMessagingSocket';
import FAQFeedback from '../components/FAQFeedback';

const TABS = ['deliveries', 'active', 'earnings', 'ledger', 'profile'];

export default function DeliveryRiderDashboard({ token, user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('deliveries');
  const [dashboard, setDashboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [available, setAvailable] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [earningsPeriod, setEarningsPeriod] = useState('week');
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [hasActiveDelivery, setHasActiveDelivery] = useState(false);
  const [canStack, setCanStack] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [riderLocation, setRiderLocation] = useState(null);
  // Proof of delivery state — PIN only (no photo)
  const [showPodModal, setShowPodModal] = useState(false);
  const [podAssignmentId, setPodAssignmentId] = useState(null);
  const [podPin, setPodPin] = useState('');
  // Pickup order number verification
  const [showPickupVerifyModal, setShowPickupVerifyModal] = useState(false);
  const [pickupOrderNumber, setPickupOrderNumber] = useState('');
  const [pickupAssignmentId, setPickupAssignmentId] = useState(null);
  // Ledger / statements state
  const [ledger, setLedger] = useState(null);
  const [statements, setStatements] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [ledgerWeek, setLedgerWeek] = useState('');
  const [complianceData, setComplianceData] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [hmrcReport, setHmrcReport] = useState(null);
  // Delivery detail view state
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const detailMapRef = useRef(null);
  const detailMapInstanceRef = useRef(null);
  const detailMarkersRef = useRef([]);
  const detailRouteRef = useRef(null);
  // Delivery score feedback
  const [deliveryScore, setDeliveryScore] = useState(null);
  // SOS / Emergency modal state
  const [showSosModal, setShowSosModal] = useState(false);
  const [sosConfirmed, setSosConfirmed] = useState(false);
  const [sosAssignmentId, setSosAssignmentId] = useState(null);
  const [sosReason, setSosReason] = useState('accident');
  const [sosDescription, setSosDescription] = useState('');
  const [sosResult, setSosResult] = useState(null);
  // SOS evidence upload
  const [sosEvidenceFiles, setSosEvidenceFiles] = useState([]);
  const [sosEvidenceUploading, setSosEvidenceUploading] = useState(false);
  const [sosEvidenceUploaded, setSosEvidenceUploaded] = useState([]);
  // Stillness check
  const [showStillnessModal, setShowStillnessModal] = useState(false);
  const [stillnessAssignmentId, setStillnessAssignmentId] = useState(null);
  // Message notification state
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [messageToast, setMessageToast] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const riderMarkerRef = useRef(null);
  const markersRef = useRef([]);
  const locationWatchRef = useRef(null);
  const pollRef = useRef(null);

  // ── WebSocket for real-time message notifications from guests ──
  const handleNewMessage = useCallback((conversationId, message) => {
    // Only notify for messages not sent by this rider
    if (message?.senderId !== user?.id) {
      setUnreadMessageCount(prev => prev + 1);
      setMessageToast({
        senderName: message?.senderName || 'Customer',
        preview: (message?.content || '').substring(0, 60),
        conversationId,
      });
      // Auto-dismiss toast after 5 seconds
      setTimeout(() => setMessageToast(null), 5000);
    }
  }, [user?.id]);

  useMessagingSocket({ onNewMessage: handleNewMessage });

  // ── Load initial data (also check for active deliveries immediately)
  useEffect(() => {
    if (!token) return;
    loadDashboard();
    loadMyDeliveries();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    if (tab === 'deliveries') loadAvailable();
    if (tab === 'active') loadMyDeliveries();
    if (tab === 'earnings') { loadEarnings(); loadCurrentWeek(); }
    if (tab === 'ledger') { loadLedger(); loadStatements(); }
    if (tab === 'compliance') loadCompliance();
    if (tab === 'profile') loadProfile();
  }, [token, tab, earningsPeriod]);

  // ── Map init when on deliveries tab (retries until Leaflet CDN is ready)
  useEffect(() => {
    if (tab !== 'deliveries' || mapInstanceRef.current) return;
    const tryInit = () => initMap();
    if (!tryInit()) {
      const retryInterval = setInterval(() => {
        if (tryInit()) clearInterval(retryInterval);
      }, 200);
      const timeout = setTimeout(() => clearInterval(retryInterval), 10000);
      return () => { clearInterval(retryInterval); clearTimeout(timeout); };
    }
  }, [tab]);

  // ── GPS tracking when online
  useEffect(() => {
    if (isOnline) {
      startGPS();
      pollRef.current = setInterval(loadAvailable, 15000);
    } else {
      stopGPS();
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => {
      stopGPS();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isOnline]);

  const loadDashboard = async () => {
    try {
      const data = await deliveryAPI.getDashboard(token);
      setDashboard(data);
      setIsOnline(data.rider?.isAvailable || false);
    } catch (e) {
      setError('Could not load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailable = async () => {
    try {
      const data = await deliveryAPI.getAvailableDeliveries(token);
      // Handle new structured response: { deliveries, hasActiveDelivery, canStack, ... }
      if (data && data.deliveries) {
        setAvailable(data.deliveries);
        setHasActiveDelivery(data.hasActiveDelivery || false);
        setCanStack(data.canStack || false);
        updateMapMarkers(data.deliveries);
      } else {
        // Fallback for old-format response (plain array)
        setAvailable(Array.isArray(data) ? data : []);
        setCanStack(false);
        updateMapMarkers(Array.isArray(data) ? data : []);
      }
    } catch (e) { console.error('Failed to load available deliveries:', e); }
  };

  const loadMyDeliveries = async () => {
    try {
      const data = await deliveryAPI.getMyDeliveries(token);
      setMyDeliveries(data);
      // Find active one
      const active = data.find(d => ['accepted', 'picked_up', 'en_route'].includes(d.status));
      setActiveDelivery(active || null);
      setHasActiveDelivery(!!active);
    } catch (e) { console.error('Failed to load my deliveries:', e); }
  };

  const loadEarnings = async () => {
    try {
      const data = await deliveryAPI.getEarnings(earningsPeriod, token);
      setEarnings(data);
    } catch (e) {}
  };

  const loadProfile = async () => {
    try {
      const data = await deliveryAPI.getProfile(token);
      setProfile(data);
    } catch (e) {}
  };

  const loadCurrentWeek = async () => {
    try {
      const data = await deliveryAPI.getCurrentWeekEarnings(token);
      setCurrentWeek(data);
    } catch (e) {}
  };

  const loadLedger = async () => {
    try {
      const data = await deliveryAPI.getLedger(ledgerWeek || undefined, token);
      setLedger(data);
    } catch (e) {}
  };

  const loadStatements = async () => {
    try {
      const data = await deliveryAPI.getStatements(token);
      setStatements(data);
    } catch (e) {}
  };

  const loadCompliance = async () => {
    setComplianceLoading(true);
    try {
      const [compliance, hmrc] = await Promise.all([
        deliveryAPI.getCompliance(token).catch(() => null),
        deliveryAPI.getHmrcReport(null, token).catch(() => null)
      ]);
      setComplianceData(compliance);
      setHmrcReport(hmrc);
    } catch (e) {
      console.error('Compliance load error:', e);
    } finally {
      setComplianceLoading(false);
    }
  };

  const loadInvoices = async (weekId) => {
    try {
      const data = await deliveryAPI.getInvoices(weekId, token);
      setInvoices(data);
    } catch (e) {}
  };

  const downloadStatement = async (weekId) => {
    try {
      const html = await deliveryAPI.downloadStatement(weekId, token);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement-${weekId}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not download statement');
    }
  };

  // ── Map
  const initMap = () => {
    if (!mapRef.current || mapInstanceRef.current || typeof window === 'undefined') return false;
    const L = window.L;
    if (!L) return false;

    const map = L.map(mapRef.current, { zoomControl: true }).setView([51.505, -0.09], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    mapInstanceRef.current = map;
    // Fix blank tiles when container was hidden or resized
    setTimeout(() => map.invalidateSize(), 200);
    return true;
  };

  const updateMapMarkers = (deliveries) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    deliveries.forEach(d => {
      if (d.restaurantLatitude && d.restaurantLongitude) {
        const icon = L.divIcon({
          html: `<div style="background:#f59e0b;color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🍽</div>`,
          className: '', iconSize: [32, 32], iconAnchor: [16, 16]
        });
        const marker = L.marker([d.restaurantLatitude, d.restaurantLongitude], { icon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="min-width:180px">
              <strong>${d.restaurantName}</strong><br/>
              ${d.deliveryAddress}<br/>
              <strong style="color:#16a34a">£${d.riderEarnings?.toFixed(2)}</strong> earnings
            </div>
          `);
        markersRef.current.push(marker);
      }
    });
  };

  const startGPS = () => {
    if (!navigator.geolocation) return;
    locationWatchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, heading, speed } = pos.coords;
        setRiderLocation({ lat, lng });
        try {
          await deliveryAPI.updateLocation(lat, lng, heading, speed, token);
        } catch (e) {}

        const L = window.L;
        if (L && mapInstanceRef.current) {
          if (!riderMarkerRef.current) {
            const icon = L.divIcon({
              html: `<div style="background:#3b82f6;color:#fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)">🛵</div>`,
              className: '', iconSize: [36, 36], iconAnchor: [18, 18]
            });
            riderMarkerRef.current = L.marker([lat, lng], { icon })
              .addTo(mapInstanceRef.current)
              .bindPopup('Your location');
          } else {
            riderMarkerRef.current.setLatLng([lat, lng]);
          }
          mapInstanceRef.current.panTo([lat, lng]);
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
  };

  const stopGPS = () => {
    if (locationWatchRef.current !== null) {
      navigator.geolocation.clearWatch(locationWatchRef.current);
      locationWatchRef.current = null;
    }
  };

  // ── Delivery Detail Map ────────────────────────────────────────────────
  const openDeliveryDetail = (delivery) => {
    setSelectedDelivery(delivery);
    // Small delay for DOM to render the detail map container
    setTimeout(() => initDetailMap(delivery), 200);
  };

  const closeDeliveryDetail = () => {
    if (detailMapInstanceRef.current) {
      detailMapInstanceRef.current.remove();
      detailMapInstanceRef.current = null;
    }
    detailMarkersRef.current = [];
    detailRouteRef.current = null;
    setSelectedDelivery(null);
    setDeliveryScore(null);
  };

  const initDetailMap = (delivery) => {
    if (!detailMapRef.current || typeof window === 'undefined') return;
    const L = window.L;
    if (!L) return;

    // Clean up previous
    if (detailMapInstanceRef.current) {
      detailMapInstanceRef.current.remove();
      detailMapInstanceRef.current = null;
    }

    const map = L.map(detailMapRef.current, { zoomControl: true }).setView([51.505, -0.09], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    detailMapInstanceRef.current = map;

    const points = [];

    // Restaurant marker
    if (delivery.restaurantLatitude && delivery.restaurantLongitude) {
      const restIcon = L.divIcon({
        html: '<div style="background:#f59e0b;color:#fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:18px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🍽</div>',
        className: '', iconSize: [36, 36], iconAnchor: [18, 18]
      });
      L.marker([delivery.restaurantLatitude, delivery.restaurantLongitude], { icon: restIcon })
        .addTo(map)
        .bindPopup(`<strong>Pickup:</strong> ${delivery.restaurantName}<br/>${delivery.restaurantAddress}`);
      points.push([delivery.restaurantLatitude, delivery.restaurantLongitude]);
    }

    // Delivery destination marker
    if (delivery.deliveryLat && delivery.deliveryLng) {
      const destIcon = L.divIcon({
        html: '<div style="background:#ef4444;color:#fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:18px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)">📍</div>',
        className: '', iconSize: [36, 36], iconAnchor: [18, 18]
      });
      L.marker([delivery.deliveryLat, delivery.deliveryLng], { icon: destIcon })
        .addTo(map)
        .bindPopup(`<strong>Drop-off:</strong> ${delivery.guestName || 'Customer'}<br/>${delivery.deliveryAddress}`);
      points.push([delivery.deliveryLat, delivery.deliveryLng]);
    }

    // Rider current location
    if (riderLocation) {
      const riderIcon = L.divIcon({
        html: '<div style="background:#3b82f6;color:#fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)">🛵</div>',
        className: '', iconSize: [36, 36], iconAnchor: [18, 18]
      });
      L.marker([riderLocation.lat, riderLocation.lng], { icon: riderIcon })
        .addTo(map)
        .bindPopup('Your location');
      points.push([riderLocation.lat, riderLocation.lng]);
    }

    // Draw route line between points
    if (points.length >= 2) {
      const routeLine = L.polyline(points, {
        color: '#3b82f6', weight: 4, opacity: 0.7, dashArray: '10, 10'
      }).addTo(map);
      detailRouteRef.current = routeLine;
      map.fitBounds(routeLine.getBounds(), { padding: [50, 50] });
    } else if (points.length === 1) {
      map.setView(points[0], 14);
    }
  };

  // Calculate delivery duration and score for completed deliveries
  const getDeliveryTimeScore = (delivery) => {
    if (!delivery.acceptedAt || !delivery.deliveredAt) return null;
    const accepted = new Date(delivery.acceptedAt);
    const delivered = new Date(delivery.deliveredAt);
    const durationMins = Math.round((delivered - accepted) / 60000);
    const distKm = delivery.distanceKm || 0;

    // Score based on delivery efficiency — no speed-related language
    let score, label, color, advice;
    if (durationMins <= 25) {
      score = 5; label = 'Excellent'; color = 'text-green-600'; advice = 'Great job! Smooth and efficient delivery.';
    } else if (durationMins <= 40) {
      score = 4; label = 'Good'; color = 'text-green-500'; advice = 'Well done. You delivered within the expected window.';
    } else if (durationMins <= 55) {
      score = 3; label = 'Fair'; color = 'text-amber-500'; advice = 'Consider planning your route before setting off to improve efficiency.';
    } else {
      score = 2; label = 'Late'; color = 'text-red-500'; advice = 'This delivery took longer than expected. Try to plan ahead and use the map for the most direct route.';
    }

    return { durationMins, distKm, score, label, color, advice };
  };

  const toggleOnline = async () => {
    try {
      const data = await deliveryAPI.toggleAvailability(token);
      setIsOnline(data.isAvailable);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not change availability');
    }
  };

  const handleAccept = async (assignmentId) => {
    // Check if delivery is acceptable: either no active delivery, or it's on-route stackable
    const delivery = available.find(d => (d.id || d._id) === assignmentId);
    if (hasActiveDelivery && !(canStack && delivery?.isOnRoute)) {
      setError('Complete your current delivery before accepting a new one');
      return;
    }
    setActionLoading(true);
    try {
      await deliveryAPI.acceptDelivery(assignmentId, token);
      // Optimistically remove accepted delivery from available list
      setAvailable(prev => prev.filter(d => (d.id || d._id) !== assignmentId));
      setHasActiveDelivery(true);
      setCanStack(false);
      await loadMyDeliveries();
      setTab('active');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not accept delivery');
      // Refresh available list in case of error
      await loadAvailable();
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async (assignmentId, status) => {
    // If marking as delivered, open delivery PIN modal
    if (status === 'delivered') {
      setPodAssignmentId(assignmentId);
      setPodPin('');
      setShowPodModal(true);
      return;
    }
    // If picking up, require order number verification
    if (status === 'picked_up') {
      setPickupAssignmentId(assignmentId);
      setPickupOrderNumber('');
      setShowPickupVerifyModal(true);
      return;
    }
    setActionLoading(true);
    try {
      await deliveryAPI.updateDeliveryStatus(assignmentId, status, token);
      await loadMyDeliveries();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePickupOrderVerify = async () => {
    if (!pickupOrderNumber.trim()) {
      setError('Please enter the 6-character pickup code from the order bag');
      return;
    }
    setActionLoading(true);
    try {
      await deliveryAPI.updateDeliveryStatus(pickupAssignmentId, 'picked_up', token, { pickupCode: pickupOrderNumber.trim().toUpperCase() });
      setShowPickupVerifyModal(false);
      setPickupOrderNumber('');
      await loadMyDeliveries();
    } catch (e) {
      setError(e.response?.data?.detail || 'Pickup code does not match. Check the code on the order bag.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProofOfDelivery = async () => {
    if (podPin.length !== 4) {
      setError('Please enter the 4-digit delivery PIN from the customer');
      return;
    }
    setActionLoading(true);
    try {
      await deliveryAPI.completeDelivery(podAssignmentId, podPin, token);
      setShowPodModal(false);
      setPodPin('');
      await loadMyDeliveries();
    } catch (e) {
      setError(e.response?.data?.detail || 'Incorrect PIN. Please ask the customer for their delivery code.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Message Guest — ensure conversation exists first ──
  const handleMessageGuest = async (delivery) => {
    const assignmentId = delivery.id || delivery._id;
    if (delivery.guestConversationId) {
      navigate(`/messages?conversationId=${delivery.guestConversationId}`);
      return;
    }
    try {
      const result = await deliveryAPI.ensureConversation(assignmentId, token);
      if (result.conversationId) {
        navigate(`/messages?conversationId=${result.conversationId}`);
      } else {
        navigate('/messages');
      }
    } catch (e) {
      console.error('Failed to create conversation:', e);
      navigate('/messages');
    }
  };

  // ── SOS / Emergency Handler ──────────────────────────────────────────
  const openSosModal = (assignmentId) => {
    setSosAssignmentId(assignmentId);
    setSosConfirmed(false);
    setSosReason('accident');
    setSosDescription('');
    setSosResult(null);
    setSosEvidenceFiles([]);
    setSosEvidenceUploading(false);
    setSosEvidenceUploaded([]);
    setShowSosModal(true);
  };

  const handleSosSubmit = async () => {
    setActionLoading(true);
    try {
      const result = await deliveryAPI.reportSOS(
        sosAssignmentId,
        sosReason,
        sosDescription || undefined,
        riderLocation?.lat || undefined,
        riderLocation?.lng || undefined,
        token
      );
      setSosResult(result);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit emergency report');
      setShowSosModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const closeSosModal = () => {
    setShowSosModal(false);
    setSosResult(null);
    loadMyDeliveries();
  };

  // ── Notification Polling (Stillness Check) ─────────────────────────
  useEffect(() => {
    if (!token) return;
    const pollNotifications = async () => {
      try {
        const notifications = await deliveryAPI.getNotifications(token);
        if (notifications && Array.isArray(notifications)) {
          const stillness = notifications.find(n => n.type === 'stillness_check' && !n.read);
          if (stillness) {
            setStillnessAssignmentId(stillness.assignmentId);
            setShowStillnessModal(true);
          }
        }
      } catch (e) { /* silent — polling */ }
    };
    pollNotifications();
    const interval = setInterval(pollNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const STATUS_FLOW = {
    accepted: { next: 'picked_up', label: 'Mark as Picked Up 📦' },
    picked_up: { next: 'en_route', label: 'En Route to Customer 🛵' },
    en_route: { next: 'delivered', label: 'Mark as Delivered ✅' },
  };

  const statusColor = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-blue-100 text-blue-800',
    picked_up: 'bg-purple-100 text-purple-800',
    en_route: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🛵</div>
          <p className="text-gray-600">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  const rider = dashboard?.rider || {};
  const onboardingStatus = rider.onboardingStatus || 'pending_documents';

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Delivery Dashboard</h1>
          <p className="text-gray-500 text-sm">{user?.name || rider.fullName}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Messages badge */}
          <button
            onClick={() => {
              setUnreadMessageCount(0);
              if (activeDelivery) {
                handleMessageGuest(activeDelivery);
              } else {
                navigate('/messages');
              }
            }}
            className="relative p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            title="Messages"
          >
            💬
            {unreadMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce">
                {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
              </span>
            )}
          </button>
          {onboardingStatus === 'approved' ? (
            <button
              onClick={toggleOnline}
              className={`px-5 py-2 rounded-full font-semibold text-white transition-all ${
                isOnline
                  ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-200'
                  : 'bg-gray-400 hover:bg-gray-500'
              }`}
            >
              {isOnline ? '🟢 Online' : '⚫ Go Online'}
            </button>
          ) : (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              onboardingStatus === 'under_review'
                ? 'bg-blue-100 text-blue-700'
                : onboardingStatus === 'rejected'
                ? 'bg-red-100 text-red-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {onboardingStatus === 'pending_documents' && '📋 Docs Pending'}
              {onboardingStatus === 'crc_pending' && '🔍 CRC Check Pending'}
              {onboardingStatus === 'under_review' && '⏳ Under Review'}
              {onboardingStatus === 'rejected' && '❌ Application Rejected'}
            </span>
          )}
        </div>
      </div>

      {/* Onboarding notice */}
      {onboardingStatus !== 'approved' && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold text-amber-900 mb-1">Application Under Review</h3>
          <p className="text-sm text-amber-700">
            {onboardingStatus === 'pending_documents' &&
              'Please upload your right-to-work documents, criminal record check consent, and insurance certificates to complete your application.'}
            {onboardingStatus === 'crc_pending' &&
              'Your criminal record check (CRC) is in progress. This typically takes 3–5 working days.'}
            {onboardingStatus === 'under_review' &&
              'Your documents are being reviewed by our compliance team. You\'ll receive an email within 2 working days.'}
            {onboardingStatus === 'rejected' &&
              'Your application was not approved at this time. Please contact hello@ariv.co.uk for details.'}
          </p>
        </div>
      )}

      {/* SOS account hold banner */}
      {dashboard?.rider?.accountPaused && (
        <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🚨</span>
            <div className="flex-1">
              <h3 className="font-bold text-red-900 mb-1">Account On Hold — SOS Review</h3>
              <p className="text-sm text-red-700 mb-2">
                Your account has been paused following an emergency report. You cannot accept deliveries or advance active orders until reinstated.
              </p>
              <p className="text-xs text-red-600 mb-3">
                Reason: {dashboard.rider.accountPausedReason || 'Emergency incident'}
              </p>
              <p className="text-xs text-red-600">
                Contact <strong>hello@ariv.co.uk</strong> to have your account reviewed and reinstated.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <span className="text-red-500">⚠️</span>
          <span className="text-red-700 text-sm">{error}</span>
          <button className="ml-auto text-red-400 hover:text-red-600" onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Today', value: `£${(dashboard?.stats?.todayEarnings || 0).toFixed(2)}`, icon: '💰' },
          { label: 'This Week', value: `£${(dashboard?.stats?.weekEarnings || 0).toFixed(2)}`, icon: '📅' },
          { label: 'Deliveries', value: dashboard?.stats?.totalDeliveries || 0, icon: '📦' },
          { label: 'Rating', value: `${(dashboard?.stats?.rating || 0).toFixed(1)} ⭐`, icon: '⭐' },
          { label: 'Reliability', value: dashboard?.stats?.reliabilityScore ?? 100, icon: '🛡️',
            color: (dashboard?.stats?.reliabilityScore ?? 100) >= 75 ? 'text-green-700' : (dashboard?.stats?.reliabilityScore ?? 100) >= 50 ? 'text-amber-700' : 'text-red-700' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl mb-1">{stat.icon}</div>
            <div className={`text-xl font-bold ${stat.color || 'text-gray-900'}`}>{stat.value}</div>
            <div className="text-xs text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 gap-1">
        {[
          { key: 'deliveries', label: '📍 Available', count: available.length },
          { key: 'active', label: '🛵 Active', count: myDeliveries.filter(d => ['accepted','picked_up','en_route'].includes(d.status)).length },
          { key: 'earnings', label: '💰 Earnings' },
          { key: 'ledger', label: '📒 Ledger' },
          { key: 'compliance', label: '🛡️ Compliance' },
          { key: 'profile', label: '👤 Profile' },
          { key: 'faq', label: '❓ FAQ' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className="ml-1.5 bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Available Deliveries Tab */}
      {tab === 'deliveries' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Map */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-gray-800">🗺 Delivery Map</span>
              {riderLocation && (
                <span className="text-xs text-green-600">📍 GPS Active</span>
              )}
            </div>
            <div ref={mapRef} style={{ height: '380px' }} />
          </div>

          {/* List */}
          <div>
            {!isOnline && onboardingStatus === 'approved' && (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-500">
                <div className="text-4xl mb-2">⚫</div>
                <p className="font-medium">You are offline</p>
                <p className="text-sm mt-1">Go online to see available deliveries</p>
                <button onClick={toggleOnline} className="mt-3 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium">
                  Go Online
                </button>
              </div>
            )}

            {isOnline && available.length === 0 && (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-500">
                <div className="text-4xl mb-2">🕐</div>
                <p className="font-medium">No deliveries right now</p>
                <p className="text-sm mt-1">We'll refresh automatically every 15 seconds</p>
              </div>
            )}

            {/* Active delivery warning/stacking banner */}
            {hasActiveDelivery && available.length > 0 && (
              <div className={`rounded-xl p-4 mb-4 ${canStack ? 'bg-blue-50 border border-blue-300' : 'bg-amber-50 border border-amber-300'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xl">{canStack ? '🔄' : '⚠️'}</span>
                  <div>
                    {canStack ? (
                      <>
                        <p className="font-semibold text-blue-800">Stacked Delivery Available</p>
                        <p className="text-sm text-blue-700 mt-0.5">You're en route — deliveries within 1 mile of your drop-off can be stacked. Off-route deliveries are greyed out.</p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-amber-800">Active Delivery In Progress</p>
                        <p className="text-sm text-amber-700 mt-0.5">Complete your current delivery before accepting a new one. Showing available deliveries in your region for visibility.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {available.map(delivery => {
              const isStackable = canStack && delivery.isOnRoute;
              const isBlocked = hasActiveDelivery && !isStackable;
              const outOfRange = delivery.withinRange === false;

              return (
                <div key={delivery.id || delivery._id} className={`bg-white rounded-xl border p-4 mb-3 transition-shadow ${
                  outOfRange ? 'border-gray-100 opacity-50 grayscale' :
                  isStackable ? 'border-blue-300 hover:shadow-md ring-1 ring-blue-200' :
                  isBlocked ? 'border-gray-100 opacity-60' :
                  'border-gray-200 hover:shadow-md'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{delivery.restaurantName}</p>
                        {isStackable && (
                          <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">On Route</span>
                        )}
                        {outOfRange && (
                          <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">Out of Range</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{delivery.restaurantAddress}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">£{delivery.riderEarnings?.toFixed(2)}</div>
                      <div className="text-xs text-gray-400">your earnings</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3 flex-wrap">
                    <span>📍 {delivery.deliveryAddress?.slice(0, 35)}…</span>
                    {/* Distance from rider to restaurant */}
                    {delivery.distanceFromRider != null && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        delivery.withinRange ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {delivery.distanceFromRider.toFixed(1)} mi away
                        {delivery.etaMinutes ? ` · ~${delivery.etaMinutes} min` : ''}
                      </span>
                    )}
                    {hasActiveDelivery && delivery.distanceFromDropoff != null && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        delivery.distanceFromDropoff <= 1.0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {delivery.distanceFromDropoff.toFixed(1)} mi from drop-off
                        {delivery.etaFromDropoff ? ` · ${delivery.etaFromDropoff} min` : ''}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAccept(delivery.id || delivery._id)}
                      disabled={actionLoading || isBlocked || outOfRange}
                      className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                        outOfRange
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : isStackable
                          ? 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'
                          : isBlocked
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-500 hover:bg-green-600 text-white disabled:opacity-50'
                      }`}
                      title={outOfRange ? 'Too far from restaurant — move closer to accept' : isBlocked ? 'This delivery is off your route' : isStackable ? 'Stack this delivery with your current one' : ''}
                    >
                      {actionLoading ? 'Accepting…'
                        : outOfRange ? '📍 Too Far — Move Within 1 Mile'
                        : isStackable ? '🔄 Stack This Delivery'
                        : isBlocked ? '🔒 Off Route — Complete Current First'
                        : 'Accept Delivery'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Active / History Tab */}
      {tab === 'active' && (
        <div>
          {/* Batched / Double Pickup banner */}
          {activeDelivery?.isBatched && (
            <div className="bg-purple-50 border border-purple-300 rounded-xl p-4 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🎯</span>
                <h4 className="font-bold text-purple-900">Double Pickup</h4>
                {activeDelivery.batchBonusFare > 0 && (
                  <span className="ml-auto bg-purple-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                    +£{activeDelivery.batchBonusFare?.toFixed(2)} bonus
                  </span>
                )}
              </div>
              <p className="text-purple-700 text-sm">
                This delivery is batched — pick up both orders, then follow the drop-off sequence on your dashboard.
              </p>
            </div>
          )}

          {/* Active delivery banner */}
          {activeDelivery && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-blue-900 text-lg">Active Delivery</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${statusColor[activeDelivery.status] || 'bg-gray-100 text-gray-700'}`}>
                  {activeDelivery.status?.replace('_', ' ')}
                </span>
              </div>

              {/* Order number reference */}
              {activeDelivery.orderNumber && (
                <div className="bg-white/70 rounded-lg px-3 py-2 mb-3 inline-block">
                  <span className="text-xs text-gray-500">Order</span>
                  <span className="ml-1 font-bold text-blue-900">#{activeDelivery.orderNumber}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-xs text-blue-500 font-medium uppercase tracking-wide">Pick Up From</p>
                  <p className="font-semibold text-blue-900 mt-0.5">{activeDelivery.restaurantName}</p>
                  <p className="text-blue-700">{activeDelivery.restaurantAddress}</p>
                  {/* Restaurant contact — call & navigate only (no messaging) */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {activeDelivery.restaurantPhone && (
                      <a href={`tel:${activeDelivery.restaurantPhone}`} className="inline-flex items-center gap-1 bg-white border border-blue-200 rounded-full px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                        📞 Call Restaurant
                      </a>
                    )}
                    {/* Navigate to restaurant via Google Maps */}
                    {activeDelivery.restaurantLatitude && activeDelivery.restaurantLongitude && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1${riderLocation ? `&origin=${riderLocation.lat},${riderLocation.lng}` : ''}&destination=${activeDelivery.restaurantLatitude},${activeDelivery.restaurantLongitude}&travelmode=driving`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-3 py-1 text-xs font-medium hover:bg-green-700 transition-colors"
                      >
                        🧭 Navigate
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-blue-500 font-medium uppercase tracking-wide">Deliver To</p>
                  {/* Guest address locked until pickup code verified */}
                  {activeDelivery.status === 'accepted' ? (
                    <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 mt-1">
                      <p className="text-sm text-gray-500 font-medium">🔒 Address hidden until pickup</p>
                      <p className="text-xs text-gray-400 mt-1">Enter the pickup code at the restaurant to unlock the delivery address and start navigation.</p>
                    </div>
                  ) : (
                    <>
                      <p className="font-semibold text-blue-900 mt-0.5">{activeDelivery.guestName}</p>
                      <p className="text-blue-700">{activeDelivery.deliveryAddress}</p>
                      {/* Guest contact */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {activeDelivery.guestPhone && (
                          <a href={`tel:${activeDelivery.guestPhone}`} className="inline-flex items-center gap-1 bg-white border border-blue-200 rounded-full px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                            📞 Call Guest
                          </a>
                        )}
                        <button
                          onClick={() => handleMessageGuest(activeDelivery)}
                          className="inline-flex items-center gap-1 bg-white border border-blue-200 rounded-full px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                          💬 Message Guest
                        </button>
                        {/* Navigate to guest via Google Maps */}
                        {activeDelivery.deliveryLat && activeDelivery.deliveryLng && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1${riderLocation ? `&origin=${riderLocation.lat},${riderLocation.lng}` : ''}&destination=${activeDelivery.deliveryLat},${activeDelivery.deliveryLng}&travelmode=driving`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-3 py-1 text-xs font-medium hover:bg-green-700 transition-colors"
                          >
                            🧭 Navigate
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="text-blue-600 font-bold text-xl">£{activeDelivery.riderEarnings?.toFixed(2)}</span>
                  <span className="text-blue-500 text-sm ml-1">earnings</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Map button to open detail view */}
                  <button
                    onClick={() => openDeliveryDetail(activeDelivery)}
                    className="px-4 py-2.5 bg-white border border-blue-300 text-blue-700 rounded-lg font-semibold text-sm hover:bg-blue-50 transition-colors"
                  >
                    🗺 View Map
                  </button>
                  {STATUS_FLOW[activeDelivery.status] && (
                    <button
                      onClick={() => handleStatusUpdate(activeDelivery.id || activeDelivery._id, STATUS_FLOW[activeDelivery.status].next)}
                      disabled={actionLoading}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50 transition-colors"
                    >
                      {actionLoading ? 'Updating…' : STATUS_FLOW[activeDelivery.status].label}
                    </button>
                  )}
                </div>
              </div>

              {/* SOS / Emergency button */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-100">
                <button
                  onClick={() => openSosModal(activeDelivery.id || activeDelivery._id)}
                  className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm transition-colors"
                >
                  🆘 Report Emergency
                </button>
              </div>
            </div>
          )}

          {/* History list */}
          <h3 className="font-semibold text-gray-800 mb-3">Delivery History</h3>
          {myDeliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-5xl mb-3">📦</div>
              <p>No deliveries yet</p>
            </div>
          ) : (
            myDeliveries.filter(d => d.status !== 'pending').map(delivery => {
              const timeScore = getDeliveryTimeScore(delivery);
              return (
                <div
                  key={delivery.id || delivery._id}
                  className={`bg-white rounded-xl border p-4 mb-3 cursor-pointer transition-all hover:shadow-md ${
                    ['accepted', 'picked_up', 'en_route'].includes(delivery.status)
                      ? 'border-blue-300 hover:border-blue-400 ring-1 ring-blue-100'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => openDeliveryDetail(delivery)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{delivery.restaurantName}</p>
                        {['accepted', 'picked_up', 'en_route'].includes(delivery.status) && (
                          <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">LIVE</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{delivery.deliveryAddress}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {delivery.createdAt ? new Date(delivery.createdAt).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        }) : ''}
                      </p>
                      {timeScore && delivery.status === 'delivered' && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs font-semibold ${timeScore.color}`}>{timeScore.label}</span>
                          <span className="text-xs text-gray-400">{timeScore.durationMins} min</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <div className={`px-2 py-1 rounded-full text-xs font-semibold mb-1 ${statusColor[delivery.status] || 'bg-gray-100 text-gray-700'}`}>
                        {delivery.status?.replace('_', ' ')}
                      </div>
                      <div className="text-green-600 font-bold">
                        £{delivery.riderEarnings?.toFixed(2)}
                      </div>
                      <p className="text-[10px] text-blue-500 mt-1">Tap for details →</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* ── Delivery Detail — Full Page View ────────────────────── */}
          {selectedDelivery && (
            <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
              {/* Back button header */}
              <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10 shadow-sm">
                <button onClick={closeDeliveryDetail} className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                  <span className="text-lg">←</span> Back to deliveries
                </button>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${statusColor[selectedDelivery.status] || 'bg-gray-100 text-gray-700'}`}>
                  {selectedDelivery.status?.replace('_', ' ')}
                </span>
              </div>

              {/* Map — full width */}
              <div ref={detailMapRef} style={{ height: '300px', width: '100%' }} />

              {/* Content */}
              <div className="max-w-lg mx-auto p-5 space-y-5">
                {/* Restaurant & delivery info */}
                <div>
                  <h2 className="font-bold text-gray-900 text-xl">{selectedDelivery.restaurantName}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {selectedDelivery.createdAt ? new Date(selectedDelivery.createdAt).toLocaleDateString('en-GB', {
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : ''}
                  </p>
                  {selectedDelivery.orderNumber && (
                    <div className="mt-2 bg-gray-50 rounded-lg px-3 py-2 inline-block">
                      <span className="text-xs text-gray-500">Order</span>
                      <span className="ml-1 font-bold text-gray-900">#{selectedDelivery.orderNumber}</span>
                    </div>
                  )}
                </div>

                {/* Route stops */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <div className="w-3 h-3 mt-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-600 uppercase font-medium">Pickup</p>
                      <p className="font-semibold text-gray-800">{selectedDelivery.restaurantName}</p>
                      <p className="text-sm text-gray-600">{selectedDelivery.restaurantAddress}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {selectedDelivery.restaurantPhone && (
                          <a href={`tel:${selectedDelivery.restaurantPhone}`} className="inline-flex items-center gap-1 bg-white border border-amber-200 rounded-full px-3 py-1 text-xs font-medium text-amber-700">
                            📞 {selectedDelivery.restaurantPhone}
                          </a>
                        )}
                        {['accepted', 'picked_up', 'en_route'].includes(selectedDelivery.status) && selectedDelivery.restaurantLatitude && selectedDelivery.restaurantLongitude && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1${riderLocation ? `&origin=${riderLocation.lat},${riderLocation.lng}` : ''}&destination=${selectedDelivery.restaurantLatitude},${selectedDelivery.restaurantLongitude}&travelmode=driving`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-3 py-1 text-xs font-medium hover:bg-green-700 transition-colors"
                          >
                            🧭 Navigate in Google Maps
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-red-50 rounded-xl p-4 border border-red-100">
                    <div className="w-3 h-3 mt-1.5 rounded-full bg-red-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-red-600 uppercase font-medium">Drop-off</p>
                      {/* Address locked until pickup code verified */}
                      {selectedDelivery.status === 'accepted' ? (
                        <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 mt-1">
                          <p className="text-sm text-gray-500 font-medium">🔒 Address unlocked after pickup</p>
                          <p className="text-xs text-gray-400 mt-1">Enter the pickup code to see the delivery address.</p>
                        </div>
                      ) : (
                        <>
                          <p className="font-semibold text-gray-800">{selectedDelivery.guestName || 'Customer'}</p>
                          <p className="text-sm text-gray-600">{selectedDelivery.deliveryAddress}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {selectedDelivery.guestPhone && (
                              <a href={`tel:${selectedDelivery.guestPhone}`} className="inline-flex items-center gap-1 bg-white border border-red-200 rounded-full px-3 py-1 text-xs font-medium text-red-700">
                                📞 {selectedDelivery.guestPhone}
                              </a>
                            )}
                            {['picked_up', 'en_route'].includes(selectedDelivery.status) && selectedDelivery.deliveryLat && selectedDelivery.deliveryLng && (
                              <a
                                href={`https://www.google.com/maps/dir/?api=1${riderLocation ? `&origin=${riderLocation.lat},${riderLocation.lng}` : ''}&destination=${selectedDelivery.deliveryLat},${selectedDelivery.deliveryLng}&travelmode=driving`}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-3 py-1 text-xs font-medium hover:bg-green-700 transition-colors"
                              >
                                🧭 Navigate in Google Maps
                              </a>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact button for active deliveries — message customer only */}
                {['accepted', 'picked_up', 'en_route'].includes(selectedDelivery.status) && (
                  <div className="space-y-2">
                    <button
                      onClick={() => { closeDeliveryDetail(); handleMessageGuest(selectedDelivery); }}
                      className="w-full py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
                    >
                      💬 Message Customer
                    </button>
                    <button
                      onClick={() => { closeDeliveryDetail(); openSosModal(selectedDelivery.id || selectedDelivery._id); }}
                      className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-colors"
                    >
                      🆘 Report Emergency
                    </button>
                  </div>
                )}

                {/* Earnings & Distance */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600">Earnings</p>
                    <p className="text-lg font-bold text-green-700">£{selectedDelivery.riderEarnings?.toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Distance</p>
                    <p className="text-lg font-bold text-gray-700">{selectedDelivery.distanceKm ? `${selectedDelivery.distanceKm.toFixed(1)} km` : '—'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Items</p>
                    <p className="text-lg font-bold text-gray-700">{selectedDelivery.itemCount || '—'}</p>
                  </div>
                </div>

                {/* Delivery Score — shown for completed deliveries */}
                {(() => {
                  const score = getDeliveryTimeScore(selectedDelivery);
                  if (!score || selectedDelivery.status !== 'delivered') return null;
                  return (
                    <div className={`rounded-xl p-4 border ${
                      score.score >= 4 ? 'bg-green-50 border-green-200' :
                      score.score === 3 ? 'bg-amber-50 border-amber-200' :
                      'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{score.score >= 4 ? '⭐' : score.score === 3 ? '⏱' : '⚠'}</span>
                          <span className={`font-bold ${score.color}`}>Delivery Score: {score.label}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-600">{score.durationMins} min</span>
                      </div>
                      <p className="text-sm text-gray-700">{score.advice}</p>
                      {score.durationMins > 40 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Tip: Use the map to plan your route before setting off. Consistent, efficient deliveries improve your rider rating.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Action button for active deliveries */}
                {['accepted', 'picked_up', 'en_route'].includes(selectedDelivery.status) && STATUS_FLOW[selectedDelivery.status] && (
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedDelivery.id || selectedDelivery._id, STATUS_FLOW[selectedDelivery.status].next);
                    }}
                    disabled={actionLoading}
                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-base transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Updating…' : STATUS_FLOW[selectedDelivery.status].label}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Earnings Tab */}
      {tab === 'earnings' && (
        <div>
          {/* Current Week Banner */}
          {currentWeek && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">Current Week ({currentWeek.weekId})</p>
                  <p className="text-3xl font-bold text-green-700 mt-1">{currentWeek.currencySymbol || '£'}{(currentWeek.totalEarned || 0).toFixed(2)}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {currentWeek.tripsCompleted || 0} trips · {currentWeek.totalDistanceKm || 0} km
                    {currentWeek.totalPayAdjustments > 0 && (
                      <span className="ml-2 text-emerald-600">· incl. {currentWeek.currencySymbol || '£'}{currentWeek.totalPayAdjustments.toFixed(2)} min-wage top-up</span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-green-500">Next payout</p>
                  <p className="text-sm font-semibold text-green-700">{currentWeek.nextPayoutDate}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-6">
            {['today', 'week', 'month', 'all'].map(p => (
              <button
                key={p}
                onClick={() => setEarningsPeriod(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize border ${
                  earningsPeriod === p
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {p === 'all' ? 'All Time' : p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>

          {earnings && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Net Earnings', value: `£${(earnings.totalEarnings || 0).toFixed(2)}`, icon: '💰', color: 'text-green-700' },
                  { label: 'Deliveries', value: earnings.totalDeliveries || 0, icon: '📦', color: 'text-blue-700' },
                  { label: 'Avg Per Delivery', value: `£${(earnings.avgPerDelivery || 0).toFixed(2)}`, icon: '📊', color: 'text-purple-700' },
                  { label: 'Platform Fee', value: `£${(earnings.platformDeducted || 0).toFixed(2)}`, icon: '🏦', color: 'text-gray-600' },
                  ...(earnings.insuranceDeducted > 0 ? [{ label: 'Insurance', value: `£${(earnings.insuranceDeducted || 0).toFixed(2)}`, icon: '🛡️', color: 'text-amber-600' }] : []),
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl mb-1">{card.icon}</div>
                    <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{card.label}</div>
                  </div>
                ))}
              </div>

              {/* Distance & payout info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <div className="text-2xl mb-1">🛣</div>
                  <div className="text-xl font-bold text-gray-700">{(earnings.totalDistanceKm || 0).toFixed(0)} km</div>
                  <div className="text-xs text-gray-500">Total Distance</div>
                </div>
                {earnings.lastPayout?.amount > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl mb-1">🏧</div>
                    <div className="text-xl font-bold text-gray-700">£{(earnings.lastPayout.amount || 0).toFixed(2)}</div>
                    <div className="text-xs text-gray-500">Last Payout ({earnings.lastPayout.weekId})</div>
                  </div>
                )}
              </div>

              {/* Recent transactions */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-800">Recent Deliveries</span>
                </div>
                {(earnings.recentDeliveries || []).length === 0 ? (
                  <div className="p-8 text-center text-gray-400">No deliveries in this period</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {(earnings.recentDeliveries || []).map((d, i) => (
                      <div key={i} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{d.restaurantName}</p>
                          <p className="text-xs text-gray-500">
                            {d.deliveredAt ? new Date(d.deliveredAt).toLocaleDateString('en-GB') : '—'}
                            {d.distanceKm > 0 && ` · ${d.distanceKm.toFixed(1)} km`}
                            {d.surgeMultiplier > 1 && ` · ⚡ ${d.surgeMultiplier}x surge`}
                          </p>
                        </div>
                        <div className="text-green-600 font-bold">+£{(d.riderEarnings || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Ledger & Statements Tab */}
      {tab === 'ledger' && (
        <div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Ledger entries */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">Financial Ledger</h3>
                <select
                  value={ledgerWeek}
                  onChange={(e) => { setLedgerWeek(e.target.value); setTimeout(loadLedger, 100); }}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                >
                  <option value="">All Weeks</option>
                  {[...Array(8)].map((_, i) => {
                    const d = new Date();
                    d.setDate(d.getDate() - i * 7);
                    const iso = d.toISOString().slice(0, 4) + '-W' + String(Math.ceil((d.getTime() - new Date(d.getFullYear(), 0, 1).getTime()) / 86400000 / 7)).padStart(2, '0');
                    return <option key={iso} value={iso}>{iso}</option>;
                  })}
                </select>
              </div>

              {ledger && (
                <>
                  {/* Balance summary */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <div className="text-lg font-bold text-green-600">{ledger.currencySymbol || '£'}{ledger.summary.totalEarnings.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Earned</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-orange-600">{ledger.currencySymbol || '£'}{ledger.summary.totalPayouts.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Paid Out</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-blue-700">{ledger.currencySymbol || '£'}{ledger.summary.balance.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Balance</div>
                      </div>
                    </div>
                  </div>

                  {/* Ledger entries list */}
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {(ledger.entries || []).length === 0 ? (
                      <div className="p-8 text-center text-gray-400">No ledger entries</div>
                    ) : (
                      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                        {ledger.entries.map((entry, i) => (
                          <div key={i} className="px-4 py-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{entry.description}</p>
                              <p className="text-xs text-gray-500">
                                {entry.weekId} · {entry.entryType === 'delivery_earning' ? '📦 Delivery' : '🏧 Payout'}
                              </p>
                            </div>
                            <div className={`font-bold ${entry.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {entry.amount >= 0 ? '+' : ''}{ledger.currencySymbol || '£'}{Math.abs(entry.amount).toFixed(2)}
                              {entry.payAdjustment > 0 && (
                                <div className="text-xs font-normal text-emerald-500">+{ledger.currencySymbol || '£'}{entry.payAdjustment.toFixed(2)} top-up</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Weekly Statements / Payouts */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">Weekly Statements</h3>
              {statements.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
                  <div className="text-4xl mb-2">📋</div>
                  <p>No payout statements yet</p>
                  <p className="text-xs mt-1">Payouts are processed weekly on Tuesdays</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {statements.map((stmt, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{stmt.weekId}</p>
                          <p className="text-xs text-gray-500">
                            {stmt.totalDeliveries} deliveries · {(stmt.totalDistanceKm || 0).toFixed(0)} km
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">{stmt.currencySymbol || stmt.currency || '£'}{(stmt.amount || 0).toFixed(2)}</div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            stmt.status === 'paid' ? 'bg-green-100 text-green-700' :
                            stmt.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {stmt.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => downloadStatement(stmt.weekId)}
                          className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium"
                        >
                          📥 Download Statement
                        </button>
                        <button
                          onClick={() => loadInvoices(stmt.weekId)}
                          className="text-xs px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 font-medium"
                        >
                          📄 View Invoices
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Invoice detail */}
              {invoices && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Delivery Invoices ({invoices.totals.count})
                  </h4>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                      {(invoices.invoices || []).map((inv, i) => (
                        <div key={i} className="px-4 py-3 flex items-center justify-between text-sm">
                          <div>
                            <p className="font-medium text-gray-800">{inv.invoiceNumber}</p>
                            <p className="text-xs text-gray-500">{inv.restaurantName} · {(inv.distanceKm || 0).toFixed(1)} km</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">£{(inv.riderEarnings || 0).toFixed(2)}</p>
                            <p className="text-xs text-gray-400">Fee: £{(inv.deliveryFee || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between text-sm font-semibold">
                      <span>Total Earnings</span>
                      <span className="text-green-600">£{(invoices.totals.totalEarnings || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Compliance & Tax Tab */}
      {tab === 'compliance' && (
        <div className="max-w-2xl mx-auto space-y-4">
          {complianceLoading ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Loading compliance data...</p>
            </div>
          ) : (
            <>
              {/* Overall Status */}
              {complianceData && (
                <div className={`rounded-xl p-4 flex items-center gap-3 ${
                  complianceData.overallStatus === 'compliant' ? 'bg-green-50 border border-green-200' :
                  complianceData.overallStatus === 'warning' ? 'bg-amber-50 border border-amber-200' :
                  'bg-red-50 border border-red-200'
                }`}>
                  <span className="text-2xl">{complianceData.overallStatus === 'compliant' ? '✅' : complianceData.overallStatus === 'warning' ? '⚠️' : '🚫'}</span>
                  <div>
                    <p className="font-bold text-sm">
                      {complianceData.overallStatus === 'compliant' ? 'Fully Compliant' :
                       complianceData.overallStatus === 'warning' ? 'Action Needed' : 'Cannot Accept Jobs'}
                    </p>
                    {complianceData.blockers?.map((b, i) => <p key={i} className="text-xs text-red-600">• {b}</p>)}
                    {complianceData.warnings?.map((w, i) => <p key={i} className="text-xs text-amber-600">• {w}</p>)}
                  </div>
                </div>
              )}

              {/* Insurance */}
              {complianceData?.insurance && (
                <div className="bg-white rounded-xl border p-4">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">🛡️ Insurance Status</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status</span>
                      <span className={`font-semibold px-2 py-0.5 rounded text-xs ${
                        complianceData.insurance.status === 'valid' ? 'bg-green-100 text-green-700' :
                        complianceData.insurance.status === 'exempt' ? 'bg-blue-100 text-blue-700' :
                        complianceData.insurance.status === 'expiring_soon' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {complianceData.insurance.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    {complianceData.insurance.expiryDate && (
                      <div className="flex justify-between"><span className="text-gray-600">Expires</span><span>{complianceData.insurance.expiryDate}</span></div>
                    )}
                    {complianceData.insurance.daysRemaining != null && (
                      <div className="flex justify-between"><span className="text-gray-600">Days left</span><span className="font-bold">{complianceData.insurance.daysRemaining}</span></div>
                    )}
                    {complianceData.insurance.message && (
                      <p className="text-xs text-gray-500 mt-1">{complianceData.insurance.message}</p>
                    )}
                  </div>
                </div>
              )}

              {/* HMRC Tax Report */}
              {hmrcReport && hmrcReport.summary && (
                <div className="bg-white rounded-xl border p-4">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">📊 HMRC Tax Year ({hmrcReport.taxYear})</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Gross Earnings</p>
                      <p className="text-lg font-bold">£{hmrcReport.summary.grossEarnings?.toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Net Earnings</p>
                      <p className="text-lg font-bold text-green-700">£{hmrcReport.summary.netEarnings?.toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Platform Fees</p>
                      <p className="text-lg font-bold text-red-600">-£{hmrcReport.summary.platformFees?.toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Total Deliveries</p>
                      <p className="text-lg font-bold">{hmrcReport.summary.totalTrips}</p>
                    </div>
                  </div>
                  {hmrcReport.taxEstimate && (
                    <div className="border-t pt-3 mt-3">
                      <h4 className="text-xs text-gray-500 uppercase font-semibold mb-2">Estimated Tax Liability</h4>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div><p className="text-[10px] text-gray-500">Income Tax</p><p className="font-bold text-sm">£{hmrcReport.taxEstimate.incomeTax?.toFixed(2)}</p></div>
                        <div><p className="text-[10px] text-gray-500">NI (Class 2+4)</p><p className="font-bold text-sm">£{((hmrcReport.taxEstimate.class2NI || 0) + (hmrcReport.taxEstimate.class4NI || 0)).toFixed(2)}</p></div>
                        <div><p className="text-[10px] text-gray-500">Take Home</p><p className="font-bold text-sm text-green-700">£{hmrcReport.taxEstimate.takeHome?.toFixed(2)}</p></div>
                      </div>
                    </div>
                  )}
                  {hmrcReport.hmrcNotes && (
                    <div className="mt-3 bg-blue-50 rounded-lg p-3 text-xs text-blue-800">
                      <p className="font-semibold mb-1">HMRC Filing Info</p>
                      <p>Filing deadline: {hmrcReport.hmrcNotes.filingDeadline}</p>
                      <p className="mt-0.5">Keep records for 5 years after filing</p>
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 mt-2 text-center">{hmrcReport.disclaimer}</p>
                </div>
              )}

              {!complianceData && !hmrcReport && (
                <div className="bg-white rounded-xl border p-8 text-center">
                  <p className="text-3xl mb-2">🛡️</p>
                  <p className="text-gray-500 text-sm">No compliance data available yet</p>
                  <button onClick={loadCompliance} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
                    Check Compliance
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Profile Tab */}
      {tab === 'profile' && profile && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Personal Information</h3>
            <div className="space-y-3 text-sm">
              {[
                { label: 'Full Name', value: profile.fullName },
                { label: 'Email', value: profile.email },
                { label: 'Phone', value: profile.phone },
                { label: 'City', value: profile.city },
                { label: 'Vehicle Type', value: (profile.vehicle?.type || profile.deliveryMethod)?.replace('_', ' ') },
                { label: 'Vehicle', value: [profile.vehicle?.make, profile.vehicle?.model, profile.vehicle?.colour].filter(Boolean).join(' ') || null },
                { label: 'Plate Number', value: profile.vehicle?.registrationPlate },
              ].filter(row => row.value).map(row => (
                <div key={row.label} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{row.label}</span>
                  <span className="font-medium text-gray-800">{row.value || '—'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* UK Compliance Documents */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">UK Compliance</h3>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Right to Work', value: profile.compliance?.rightToWork?.status?.replace('_', ' ') || profile.compliance?.rightToWorkStatus?.replace('_', ' ') },
                { label: 'CRC Consent', value: profile.compliance?.criminalRecordCheck?.consented || profile.compliance?.crcConsent ? '✅ Consented' : '❌ Not consented' },
                { label: 'NI Number', value: profile.compliance?.nationalInsuranceNumber ? '••••••••' : 'Not provided' },
                { label: 'Food Hygiene', value: profile.compliance?.foodHygiene?.certificateLevel ? `Level ${profile.compliance.foodHygiene.certificateLevel.replace('level_', '')}` : 'Not provided' },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{row.label}</span>
                  <span className="font-medium text-gray-800 capitalize">{row.value || '—'}</span>
                </div>
              ))}

              {/* Driving Licence — shown for motorized vehicles */}
              {!['bicycle', 'ebike'].includes(profile.deliveryMethod) && (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">Driving Licence</span>
                    <span className="font-medium text-gray-800">
                      {profile.compliance?.drivingLicense?.number ? `…${profile.compliance.drivingLicense.number.slice(-4)}` : (profile.compliance?.drivingLicenseNumber ? `…${profile.compliance.drivingLicenseNumber.slice(-4)}` : '—')}
                      {profile.compliance?.drivingLicense?.category && ` (Cat. ${profile.compliance.drivingLicense.category})`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">Licence Expiry</span>
                    <span className="font-medium text-gray-800">{profile.compliance?.drivingLicense?.expiry || profile.compliance?.drivingLicenseExpiry || '—'}</span>
                  </div>
                </>
              )}

              {/* CBT — moped/motorcycle */}
              {['moped', 'motorcycle'].includes(profile.deliveryMethod) && (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">CBT Certificate</span>
                    <span className="font-medium text-gray-800">{profile.compliance?.cbt?.certificateNumber ? `…${profile.compliance.cbt.certificateNumber.slice(-4)}` : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">CBT Expiry</span>
                    <span className="font-medium text-gray-800">{profile.compliance?.cbt?.expiryDate || '—'}</span>
                  </div>
                </>
              )}

              {/* Vehicle Insurance — motorized only */}
              {!['bicycle', 'ebike'].includes(profile.deliveryMethod) && (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">Vehicle Insurance</span>
                    <span className="font-medium text-gray-800">
                      {profile.compliance?.insurance?.provider || profile.compliance?.insurance?.type?.replace('_', ' ') || '—'}
                      {profile.compliance?.insurance?.coverType === 'hire_and_reward' && (
                        <span className="ml-1 text-xs text-green-600">(H&R)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">Insurance Expiry</span>
                    <span className="font-medium text-gray-800">{profile.compliance?.insurance?.expiry || profile.compliance?.vehicleInsuranceExpiry || '—'}</span>
                  </div>
                </>
              )}

              {/* Public Liability */}
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-gray-500">Public Liability</span>
                <span className="font-medium text-gray-800">{profile.compliance?.publicLiability?.hasInsurance ? '✅ Covered' : '❌ Not covered'}</span>
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Application Status:</strong>{' '}
                  <span className="capitalize font-semibold">{profile.onboardingStatus?.replace('_', ' ')}</span>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  To update compliance documents, contact hello@ariv.co.uk
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Registration */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">🚗 Vehicle Registration</h3>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Vehicle Type', value: (profile.vehicle?.type || profile.deliveryMethod)?.replace('_', ' ') },
                { label: 'Make & Model', value: [profile.vehicle?.make, profile.vehicle?.model].filter(Boolean).join(' ') || null },
                { label: 'Colour', value: profile.vehicle?.colour },
                { label: 'Year', value: profile.vehicle?.year },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{row.label}</span>
                  <span className="font-medium text-gray-800 capitalize">{row.value || '—'}</span>
                </div>
              ))}

              {/* Registration plate — motorized vehicles only */}
              {profile.vehicle?.registrationRequired && (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">Registration Plate</span>
                    <span className="font-bold text-gray-900 bg-yellow-100 px-3 py-1 rounded-md border border-yellow-300 tracking-wider">
                      {profile.vehicle?.registrationPlate || '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">V5C Reference</span>
                    <span className="font-medium text-gray-800">{profile.vehicle?.v5cReference ? `…${profile.vehicle.v5cReference.slice(-6)}` : '—'}</span>
                  </div>
                </>
              )}

              {/* MOT — motorized vehicles */}
              {profile.vehicle?.mot?.required && (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">MOT Expiry</span>
                    <span className="font-medium text-gray-800">{profile.vehicle?.mot?.expiry || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500">MOT Test Number</span>
                    <span className="font-medium text-gray-800">{profile.vehicle?.mot?.testNumber ? `…${profile.vehicle.mot.testNumber.slice(-6)}` : '—'}</span>
                  </div>
                </>
              )}

              {/* Road Tax — motorized vehicles */}
              {profile.vehicle?.roadTax?.required && (
                <div className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">Road Tax Expiry</span>
                  <span className="font-medium text-gray-800">{profile.vehicle?.roadTax?.expiry || '—'}</span>
                </div>
              )}

              {/* E-bike compliance note */}
              {profile.deliveryMethod === 'ebike' && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-700">
                    <strong>E-Bike (EAPC) Requirements:</strong> Your e-bike must have pedals, max 250W motor, and cut off at 15.5 mph.
                    Non-compliant e-bikes are classified as motor vehicles and require full registration, insurance, and a licence.
                  </p>
                </div>
              )}

              {/* Legal notice */}
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-[10px] text-gray-500">
                  Per the Road Traffic Act 1988, all motorised vehicles used for delivery must have valid registration, MOT (if 3+ years old),
                  road tax, and hire & reward insurance. Failure to comply may result in fines up to £1,000, 6-8 penalty points, and vehicle seizure.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <h3 className="font-semibold text-gray-800 mb-3">Navigation</h3>
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100">
                🏠 Guest Portal
              </button>
              {onLogout && (
                <button onClick={() => { onLogout(); navigate('/'); }} className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100">
                  🚪 Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── FAQ Tab */}
      {tab === 'faq' && (
        <FAQFeedback />
      )}

      {/* ── Pickup PIN Modal (driver enters restaurant's PIN) */}
      {/* ── Order Number Verification at Pickup */}
      {showPickupVerifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Confirm Pickup</h3>
            <p className="text-sm text-gray-500 mb-4">
              Enter the <strong>6-character pickup code</strong> printed on the order bag to verify you have the correct order.
            </p>
            <input
              type="text"
              value={pickupOrderNumber}
              onChange={e => setPickupOrderNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
              placeholder="e.g. A3B7X2"
              maxLength={6}
              className="w-full text-center text-3xl font-bold tracking-[8px] py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 mb-2"
              autoFocus
            />
            <p className="text-xs text-gray-400 text-center mb-4">
              This code is attached to the order bag by the restaurant. Ask the restaurant staff if you can't find it.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowPickupVerifyModal(false); setPickupOrderNumber(''); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePickupOrderVerify}
                disabled={pickupOrderNumber.trim().length < 6 || actionLoading}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Verifying…' : 'Confirm Pickup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delivery PIN Modal — PIN Only */}
      {showPodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Delivery Confirmation</h3>
            <p className="text-sm text-gray-500 mb-4">
              Ask the customer for their 4-digit delivery code to complete the order.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={podPin}
              onChange={(e) => setPodPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-full text-center text-3xl font-bold tracking-[12px] border-2 border-gray-300 rounded-xl p-4 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
              placeholder="- - - -"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-2 text-center">
              The customer received this code when their order was dispatched.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowPodModal(false); setPodPin(''); }}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleProofOfDelivery}
                disabled={actionLoading || podPin.length !== 4}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Verifying…' : 'Confirm Delivery'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SOS / Emergency Modal (3-step: confirm → form → result) ──── */}
      {showSosModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            {/* Step 1: Confirmation — "Are you sure?" */}
            {!sosConfirmed && !sosResult && (
              <>
                <div className="text-center mb-4">
                  <div className="text-5xl mb-3">⚠️</div>
                  <h3 className="text-lg font-bold text-gray-900">Are you sure?</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Reporting an emergency will:
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                  <ul className="text-sm text-red-700 space-y-2">
                    <li className="flex items-start gap-2"><span>&#8226;</span> Pause your account until reviewed</li>
                    <li className="flex items-start gap-2"><span>&#8226;</span> Reassign your current delivery</li>
                    <li className="flex items-start gap-2"><span>&#8226;</span> Notify the guest and restaurant</li>
                    <li className="flex items-start gap-2"><span>&#8226;</span> Generate an insurance report</li>
                  </ul>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSosModal(false)}
                    className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                  >
                    No, go back
                  </button>
                  <button
                    onClick={() => setSosConfirmed(true)}
                    className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors"
                  >
                    Yes, I need help
                  </button>
                </div>
              </>
            )}

            {/* Step 2: SOS Form — reason + description */}
            {sosConfirmed && !sosResult && (
              <>
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">🆘</div>
                  <h3 className="text-lg font-bold text-red-700">Report Emergency</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Your account will be paused and the delivery will be reassigned. The guest will be notified.
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">What happened?</label>
                <select
                  value={sosReason}
                  onChange={(e) => setSosReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5 mb-3 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none"
                >
                  <option value="accident">Road accident / collision</option>
                  <option value="vehicle_breakdown">Vehicle breakdown</option>
                  <option value="medical">Medical emergency</option>
                  <option value="other">Other emergency</option>
                </select>

                <label className="block text-sm font-medium text-gray-700 mb-1">Details (optional)</label>
                <textarea
                  value={sosDescription}
                  onChange={(e) => setSosDescription(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm resize-none focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none"
                  placeholder="Briefly describe what happened..."
                />

                <div className="flex gap-3 mt-5">
                  <button
                    onClick={() => setSosConfirmed(false)}
                    className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSosSubmit}
                    disabled={actionLoading}
                    className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {actionLoading ? 'Submitting…' : 'Submit SOS'}
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Result — emergency numbers + insurance + evidence upload */}
            {sosResult && (
              <>
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">✅</div>
                  <h3 className="text-lg font-bold text-gray-900">Emergency Reported</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Your delivery has been flagged and your account is paused. The guest and restaurant have been notified.
                  </p>
                </div>

                {sosResult.emergencyNumbers && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                    <p className="text-xs text-red-600 font-semibold uppercase mb-2">Emergency Numbers</p>
                    {Object.entries(sosResult.emergencyNumbers).map(([key, num]) => (
                      <a key={key} href={`tel:${num}`} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-gray-700">{key === 'police' ? 'Police' : key === 'ambulance' ? 'Ambulance' : key === 'nonEmergency' ? 'Non-Emergency' : 'Zego Insurance'}</span>
                        <span className="font-bold text-red-700">{num}</span>
                      </a>
                    ))}
                  </div>
                )}

                {sosResult.tripSnapshot && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                    <p className="text-xs text-blue-600 font-semibold">Insurance Claim Reference</p>
                    <p className="text-sm font-bold text-blue-900 mt-0.5">{sosResult.tripSnapshot?.assignmentId || 'Pending'}</p>
                    <p className="text-xs text-blue-500 mt-1">A trip snapshot has been sent to your insurer.</p>
                  </div>
                )}

                {/* Evidence Upload Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                  <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Upload Evidence</p>
                  <p className="text-xs text-gray-500 mb-3">Upload photos, receipts, or medical documents as evidence for your claim.</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={(e) => setSosEvidenceFiles(Array.from(e.target.files))}
                    className="w-full text-xs text-gray-600 mb-2 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {sosEvidenceFiles.length > 0 && (
                    <div className="text-xs text-gray-500 mb-2">
                      {sosEvidenceFiles.length} file(s) selected
                    </div>
                  )}
                  {sosEvidenceUploaded.length > 0 && (
                    <div className="space-y-1 mb-2">
                      {sosEvidenceUploaded.map((f, i) => (
                        <div key={i} className="text-xs text-green-600 flex items-center gap-1">
                          <span>✅</span> {f.filename || f}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (sosEvidenceFiles.length === 0) return;
                      setSosEvidenceUploading(true);
                      try {
                        const result = await deliveryAPI.uploadSosEvidence(sosAssignmentId, sosEvidenceFiles, token);
                        setSosEvidenceUploaded(prev => [...prev, ...(result.files || result.uploaded || [{ filename: 'Uploaded' }])]);
                        setSosEvidenceFiles([]);
                      } catch (e) {
                        setError(e.response?.data?.detail || 'Failed to upload evidence');
                      } finally {
                        setSosEvidenceUploading(false);
                      }
                    }}
                    disabled={sosEvidenceFiles.length === 0 || sosEvidenceUploading}
                    className="w-full py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {sosEvidenceUploading ? 'Uploading…' : 'Upload Evidence'}
                  </button>
                </div>

                <button
                  onClick={closeSosModal}
                  className="w-full py-2.5 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Stillness Check Modal ─────────────────────────────────────── */}
      {showStillnessModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🟡</div>
              <h3 className="text-lg font-bold text-gray-900">Are you alright?</h3>
              <p className="text-sm text-gray-500 mt-2">
                You've been stationary for 10 minutes. Please confirm you're OK.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
              <p className="text-xs text-amber-700 font-medium">
                If you don't respond within 5 minutes, an emergency alert will be sent automatically to admin and the guest will be notified.
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  await deliveryAPI.confirmStillness(stillnessAssignmentId, token);
                  setShowStillnessModal(false);
                  setStillnessAssignmentId(null);
                } catch (e) {
                  setError(e.response?.data?.detail || 'Could not confirm');
                }
              }}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors text-base"
            >
              I'm OK ✓
            </button>
          </div>
        </div>
      )}

      {/* ── Floating message notification toast ── */}
      {messageToast && (
        <div
          className="fixed top-4 right-4 z-[100] bg-white border border-blue-200 shadow-2xl rounded-xl p-4 max-w-xs animate-bounce cursor-pointer"
          onClick={() => {
            setMessageToast(null);
            setUnreadMessageCount(0);
            if (activeDelivery) handleMessageGuest(activeDelivery);
            else navigate('/messages');
          }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-lg flex-shrink-0">💬</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">{messageToast.senderName}</p>
              <p className="text-gray-600 text-xs truncate">{messageToast.preview}</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setMessageToast(null); }}
              className="text-gray-400 hover:text-gray-600 text-sm"
            >✕</button>
          </div>
        </div>
      )}

    </div>
  );
}
